import {
  AngleDown,
  Close,
  LockFilled,
  Plus,
  TrackArticle,
  TrackAssessment,
  TrackFile,
  TrackImportedContent,
  TrackScorm,
  TrackVideo,
} from '@/client/components/icons/ContinuIcons';
import {
  Box,
  Button,
  ButtonGroup,
  ComponentWithAs,
  Flex,
  HStack,
  Icon,
  IconButton,
  IconProps,
  Image,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  ContentTypes,
  WorkflowReponseType,
  WorkflowResponse,
} from '@/client/services/api/graphql/gql/graphql';
import { Fragment, useEffect, useState } from 'react';

import AssignmentBuilderFormContainer from '../layout/AssignmentBuilderFormContainer';
import type { AssignmentFormData } from '../../types/assignmentTypes';
import { BsPlusCircle } from 'react-icons/bs';
import ContentSearch from '../../../shared/search/ContentSearch';
import OutlineButton from '@/client/components/shared/buttons/OutlineButton';
import { useAdminAssignmentsStore } from '../../state/adminAssignmentsStore';
import { useContentSearch } from '@/client/components/admin/shared/search/useContentSearch';
import { useDebounceValue } from 'usehooks-ts';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function AssignmentContentForm() {
  const { t } = useTranslation();
  const { id } = useParams();
  const {
    watch,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext<AssignmentFormData>();
  const { contentTypeOptions } = useContentSearch(true);
  const { setActiveForm } = useAdminAssignmentsStore();
  const [showSearch, setShowSearch] = useState(false);

  const [selectedContentType, setSelectedContentType] = useState<{
    name: string;
    value: string | null;
  } | null>(contentTypeOptions[0]);
  const [searchTerm, setSearchTerm] = useDebounceValue('', 300);

  useEffect(() => {
    setActiveForm('content');
  }, [setActiveForm]);

  const hasError = !!errors?.settings?.responses;

  const { settings, simpleType } = watch();

  const isLocked = !!id && simpleType === 'pointInTime';

  const { responses } = settings;

  const showSelections = responses.length > 0 && !showSearch;

  const [newSelectedOptions, setNewSelectedOptions] = useState<WorkflowResponse[]>([]);

  const contentTypeIconMap: Record<string, ComponentWithAs<'svg', IconProps>> = {
    [ContentTypes.Article]: TrackArticle,
    article: TrackArticle,
    [ContentTypes.Assessment]: TrackAssessment,
    assessment: TrackAssessment,
    [ContentTypes.File]: TrackFile,
    file: TrackFile,
    [ContentTypes.Scorm]: TrackScorm,
    scorm: TrackScorm,
    [ContentTypes.Video]: TrackVideo,
    video: TrackVideo,
    [ContentTypes.ImportedContent]: TrackImportedContent,
    imported_content: TrackImportedContent,
    [ContentTypes.Journey]: TrackArticle,
    journey: TrackArticle,
    [ContentTypes.Track]: TrackArticle,
    track: TrackArticle,
    [ContentTypes.Workshop]: TrackArticle,
    workshop: TrackArticle,
    [ContentTypes.MultiSessionWorkshop]: TrackArticle,
    multi_session_workshop: TrackArticle,
  };

  const responseTypeMap: { [key: string]: { label: string; value: string } } = {
    ASSIGNMENT: {
      label: t('admin.assignments.content.assign'),
      value: 'ASSIGNMENT',
    },
    SHARE: {
      label: t('admin.assignments.content.share'),
      value: 'SHARE',
    },
  };

  const handleItemClick = (item: any, isSelected: boolean | undefined) => {
    if (isSelected) {
      // Check if selected item is already in the newSelectedOptions array
      const existsInNewSelectedOptions = newSelectedOptions.some(
        (response: WorkflowResponse) => response.content?.id === item.id,
      );

      // Check if exists in responses
      const existsInResponses = responses.some(
        (response: WorkflowResponse) => response.content?.id === item.id,
      );

      if (existsInNewSelectedOptions) {
        setNewSelectedOptions(
          newSelectedOptions.filter(
            (response: WorkflowResponse) => response.content?.id !== item.id,
          ),
        );

        return;
      }

      if (existsInResponses) {
        setValue(
          'settings.responses',
          responses.filter((response: WorkflowResponse) => response.content?.id !== item.id),
        );

        return;
      }

      return;
    }

    const isRecompletable =
      (item.type === ContentTypes.Workshop && item.allow_reregistration === false) ||
      (item.type === 'workshop' && item.allow_reregistration === false) ||
      item.type === ContentTypes.MultiSessionWorkshop ||
      item.type === 'multi_session_workshop' ||
      item.type === ContentTypes.ImportedContent ||
      item.type === 'imported_content'
        ? 'false'
        : 'true';

    setNewSelectedOptions([
      ...newSelectedOptions,
      {
        content: {
          id: item._id,
          contentType: item.type,
          title: item.title || item.name,
          isRecompletable,
        },
        type: WorkflowReponseType.Assignment,
      },
    ]);
  };

  const checkItemIsSelected = (item: any) => {
    const isSelected =
      newSelectedOptions.some((response: WorkflowResponse) => response.content?.id === item.id) ||
      responses.some((response: WorkflowResponse) => response.content?.id === item.id);

    return isSelected;
  };

  return (
    <AssignmentBuilderFormContainer
      headerComponent={
        <Flex justifyContent="space-between" alignItems="center">
          <Stack spacing={0}>
            <HStack alignItems="center">
              {isLocked && <LockFilled boxSize={6} />}

              <Text fontSize="24px" fontWeight={600}>
                {isLocked
                  ? t('admin.assignments.content.addContent.header.locked')
                  : t('admin.assignments.content.addContent.header')}
              </Text>
            </HStack>

            {hasError && (
              <Text color="error" fontSize="14px" fontWeight={400}>
                {t('admin.assignments.content.error')}
              </Text>
            )}
          </Stack>

          {!showSearch && showSelections && (
            <OutlineButton leftIcon={<Plus boxSize={3} />} onClick={() => setShowSearch(true)}>
              {t('admin.assignments.content.addContent')}
            </OutlineButton>
          )}
        </Flex>
      }
      formComponent={
        <Box position="relative" height="full" overflowY="auto">
          <Box overflowY="auto">
            {!showSearch && !showSelections && (
              <>
                <VStack marginTop={20} paddingX={24} textAlign="center">
                  <Image src="/public/images/multiple-content.svg" alt="Multiple Content" />

                  <Text fontSize="18px" fontWeight={600}>
                    {t('admin.assignments.content.addContent')}
                  </Text>

                  <Text fontSize="16px" fontWeight={400}>
                    {t('admin.assignments.content.addContent.helpText')}
                  </Text>
                </VStack>

                <Button
                  variant="warmNeutralDashedBorder"
                  borderColor={hasError ? 'error' : 'warmNeutral.200'}
                  color={hasError ? 'error' : 'warmNeutral.800'}
                  width="full"
                  marginTop={10}
                  leftIcon={<BsPlusCircle />}
                  onClick={() => {
                    clearErrors('settings.responses');
                    setShowSearch(true);
                  }}
                >
                  {t('admin.assignments.content.addContent')}
                </Button>
              </>
            )}

            {!showSearch && showSelections && (
              <Stack spacing={2}>
                {responses.map((response: WorkflowResponse) => {
                  const isTypeSelectionDisabled =
                    response.content?.contentType === ContentTypes.Assessment ||
                    // @ts-ignore
                    response.content?.contentType === 'assessment' ||
                    response.content?.contentType === ContentTypes.Journey ||
                    // @ts-ignore
                    response.content?.contentType === 'journey';

                  return (
                    <Flex key={response.content?.id} gap={2}>
                      <Box
                        backgroundColor="warmNeutral.0"
                        border="1px solid"
                        borderColor="warmNeutral.200"
                        borderRadius="6px"
                        padding={3}
                        flex={1}
                      >
                        <HStack>
                          <Icon
                            as={contentTypeIconMap[response.content?.contentType as string]}
                            boxSize={6}
                          />

                          <Text fontSize="14px" fontWeight={500}>
                            {response.content?.title}
                          </Text>
                        </HStack>
                      </Box>

                      <Menu>
                        <MenuButton
                          padding={2}
                          paddingY={4}
                          minHeight="full"
                          height="full"
                          variant="createGoldOutline"
                          fontSize="sm"
                          width={28}
                          as={Button}
                          rightIcon={<AngleDown boxSize={2.5} marginLeft={4} />}
                          isDisabled={isTypeSelectionDisabled}
                        >
                          {responseTypeMap[response.type].label}
                        </MenuButton>

                        <MenuList
                          paddingY={2}
                          paddingX={4}
                          border="1px solid"
                          borderColor="warmNeutral.200"
                          borderRadius="6px"
                          minWidth={0}
                          width="fit-content"
                        >
                          {Object.entries(responseTypeMap).map(([key, value], index) => (
                            <Fragment key={key}>
                              <MenuItem
                                key={key}
                                fontSize="14px"
                                fontWeight={500}
                                borderRadius="4px"
                                _hover={{ backgroundColor: 'warmNeutral.100' }}
                                onClick={() => {
                                  setValue(
                                    'settings.responses',
                                    responses.map((res) => {
                                      if (res.content?.id === response.content?.id) {
                                        return {
                                          ...res,
                                          type: value.value as WorkflowReponseType,
                                        };
                                      }

                                      return res;
                                    }),
                                  );
                                }}
                              >
                                {(value as { label: string; value: string }).label}
                              </MenuItem>

                              {index !== Object.entries(responseTypeMap).length - 1 && (
                                <MenuDivider color="warmNeutral.200" />
                              )}
                            </Fragment>
                          ))}
                        </MenuList>
                      </Menu>

                      <IconButton
                        variant="unstyled"
                        size="xs"
                        icon={<Close boxSize={3} />}
                        aria-label={`${t('admin.assignments.content.removeContent.aria')}`}
                        color="neutral.500"
                        _hover={{ color: 'neutral.700' }}
                        onClick={() => handleItemClick(response.content, true)}
                        marginLeft={2}
                        alignSelf="center"
                      />
                    </Flex>
                  );
                })}
              </Stack>
            )}

            {showSearch && (
              <Stack spacing={4} paddingBottom={12}>
                <ContentSearch
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  selectedContentType={selectedContentType}
                  setSelectedContentType={setSelectedContentType}
                  handleItemClick={handleItemClick}
                  checkItemIsSelected={checkItemIsSelected}
                  includeAdvancedContentTypes
                />
              </Stack>
            )}
          </Box>
        </Box>
      }
      bottomAddonComponent={
        showSearch && (
          <ButtonGroup
            width="full"
            position="sticky"
            paddingY={4}
            paddingX={6}
            bottom={0}
            gap={2}
            backgroundColor="white"
            boxShadow="0px -4px 12px rgba(0, 0, 0, 0.05)"
          >
            <Button
              variant="createCancel"
              onClick={() => {
                setSearchTerm('');
                setNewSelectedOptions([]);
                setShowSearch(false);
              }}
            >
              {t('global.actions.cancel')}
            </Button>

            <Button
              isDisabled={newSelectedOptions.length === 0}
              onClick={() => {
                if (newSelectedOptions.length > 0) {
                  clearErrors('settings.responses');
                }

                setValue('settings.responses', [...responses, ...newSelectedOptions]);
                setNewSelectedOptions([]);
                setShowSearch(false);
              }}
            >
              {t('admin.assignments.content.saveSelections')}
            </Button>
          </ButtonGroup>
        )
      }
    />
  );
}
